/**
 * El catálogo de permisos, espejado del servidor. **Los ids y nada más.**
 *
 * La lista cerrada vive en `backend/api/v2/permisos.py` y se copia acá por la
 * misma razón por la que `MODULES` se copia en `tenant_mapping.py`: el navegador
 * tiene que poder dibujar veintidós tildes agrupados sin pedirle al servidor un
 * catálogo que no cambia nunca. Hay una prueba del backend que se pone roja si
 * las dos listas dejan de decir lo mismo (`tests/api/test_v2_permisos.py`).
 *
 * **Lo que NO se copia es la resolución.** Los presets, la suma y la resta de
 * los ajustes y el cruce con lo que la organización tiene se quedan del lado del
 * servidor y llegan ya resueltos en el snapshot de sesión (`SessionSnapshot.
 * permisos`). Derivar acá lo que la compuerta decide allá es exactamente cómo
 * nace un botón que la interfaz muestra y el servidor rechaza — que es peor que
 * un botón ausente, y fue la corrección de `seccionEditable` del 2026-09-21.
 *
 * Las etiquetas sí son de acá: son la redacción de la pantalla, no el modelo.
 *
 * Módulo PURO.
 */

/** El grupo de permisos, tal como se lee. No cambia nada de la resolución. */
export type GrupoDePermisos = 'registros' | 'comunicacion' | 'configuracion' | 'plataforma';

/**
 * EL catálogo, en el orden en que se muestra. Los ids son los del servidor;
 * la prueba de deriva los compara uno a uno, grupo por grupo.
 */
export const CATALOGO_DE_PERMISOS = [
  {
    id: 'registros',
    label: 'Registros',
    permisos: [
      'registros.ver',
      'registros.editar',
      'registros.eliminar',
      'registros.importar',
      'registros.exportar',
      'registros.masivo',
    ],
  },
  {
    id: 'comunicacion',
    label: 'Comunicación',
    permisos: [
      'conversaciones.responder',
      'correo.enviar',
      'tickets.trabajar',
      'notas.escribir',
    ],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    permisos: [
      'config.organizacion',
      'config.embudos',
      'config.campos',
      'config.etiquetas',
      'config.integraciones',
      'config.equipos',
      'config.vistas',
      'config.agentes',
      'config.usuarios',
    ],
  },
  {
    id: 'plataforma',
    label: 'Plataforma',
    permisos: ['plataforma.organizaciones', 'plataforma.modulos', 'plataforma.cuentas'],
  },
] as const satisfies ReadonlyArray<{
  id: GrupoDePermisos;
  label: string;
  permisos: readonly string[];
}>;

export type Permiso = (typeof CATALOGO_DE_PERMISOS)[number]['permisos'][number];

export const PERMISOS: readonly Permiso[] = CATALOGO_DE_PERMISOS.flatMap(
  (grupo) => grupo.permisos as readonly Permiso[],
);

/**
 * Administrar las cuentas de la organización. Con nombre porque lo nombran tres
 * lugares —la sección de Ajustes, su compuerta de guardado y la pantalla que
 * reparte los tildes— y una cadena suelta repetida tres veces es una errata
 * esperando.
 */
export const PERMISO_CUENTAS = 'config.usuarios' satisfies Permiso;

export const PERMISO_AGENTES = 'config.agentes' satisfies Permiso;

/**
 * Lo nuestro, y **jamás otorgable desde el producto**: se conceden a mano en
 * Firestore, por el rol. La pantalla de una cuenta no los dibuja nunca — ni
 * siquiera a un super, que los tiene todos: el servidor los rechaza por nombre
 * al guardar (`normalizar_ajustes`), así que un tilde ahí sería un control que
 * siempre falla.
 */
export const GRUPO_DE_PLATAFORMA: GrupoDePermisos = 'plataforma';

/** Los tres grupos que la pantalla de una cuenta sí dibuja. */
export const GRUPOS_OTORGABLES = CATALOGO_DE_PERMISOS.filter(
  (grupo) => grupo.id !== GRUPO_DE_PLATAFORMA,
);

/**
 * Cómo se llama cada permiso en pantalla.
 *
 * `Record<Permiso, string>` a propósito: un permiso nuevo en el catálogo sin su
 * etiqueta no compila, que es mejor que una casilla titulada `registros.masivo`.
 * Se escriben en infinitivo y desde la acción, no desde el objeto: quien reparte
 * permisos está decidiendo qué puede **hacer** alguien.
 */
export const ETIQUETAS_DE_PERMISO: Record<Permiso, string> = {
  'registros.ver': 'Ver los registros',
  'registros.editar': 'Crear y editar registros',
  'registros.eliminar': 'Eliminar registros',
  'registros.importar': 'Importar registros',
  'registros.exportar': 'Exportar registros',
  'registros.masivo': 'Editar registros en masa',
  'conversaciones.responder': 'Responder conversaciones',
  'correo.enviar': 'Enviar correos',
  'tickets.trabajar': 'Trabajar tickets',
  'notas.escribir': 'Escribir notas',
  'config.organizacion': 'Configurar la organización',
  'config.embudos': 'Configurar los embudos',
  'config.campos': 'Configurar los campos',
  'config.etiquetas': 'Configurar las etiquetas',
  'config.integraciones': 'Configurar las integraciones',
  'config.equipos': 'Configurar los equipos',
  'config.vistas': 'Configurar las vistas compartidas',
  'config.agentes': 'Administrar los agentes',
  'config.usuarios': 'Administrar las cuentas',
  'plataforma.organizaciones': 'Administrar organizaciones',
  'plataforma.modulos': 'Encender módulos y funciones',
  'plataforma.cuentas': 'Administrar cuentas de plataforma',
};

/**
 * Por qué un tilde está apagado y no se puede cambiar. `null` = se puede.
 *
 * Son TRES motivos y no uno, y se arreglan distinto: uno se resuelve hablando con
 * nosotros, otro con quien administra la cuenta y el tercero con quien quitó el
 * permiso. Un cartel único mandaría a la mitad de la gente a pedirle a la persona
 * equivocada.
 *
 * **Y ninguno esconde el tilde**, que es la regla que `CapabilityGate` ya aplica
 * en Ajustes: esconderlo es lo que hace que nadie entienda por qué no puede
 * algo — quien administra busca el permiso que falta, no lo encuentra, y el que
 * faltaba no era un permiso sino el plan.
 */
export type MotivoDeTildeApagado = 'organizacion' | 'quien-edita' | 'autoridad' | 'cuenta-superior';

export const MOTIVOS_DE_TILDE_APAGADO: Record<MotivoDeTildeApagado, string> = {
  organizacion: 'Está apagado para tu organización.',
  'quien-edita': 'No puedes conceder un permiso que tu cuenta no tiene.',
  autoridad: 'Lo quitó alguien con más autoridad que tu cuenta.',
  'cuenta-superior': 'Esta cuenta puede más que la tuya: no puedes quitarle permisos.',
};

/** Lo que la sesión sabe de quien mira, para decidir cada tilde. */
export interface AlcanceDePermisos {
  /** Lo que la ORGANIZACIÓN sostiene. Sin esto no se puede decir el primer motivo. */
  disponibles: ReadonlySet<Permiso>;
  /** Lo que quien edita puede CONCEDER. Nadie otorga lo que no tiene. */
  otorgables: ReadonlySet<Permiso>;
}

/**
 * Si este tilde se puede cambiar, y si no, por qué. **La única copia de la regla.**
 *
 * Dos cosas que no son obvias y las dos vienen del servidor:
 *
 * 1. **La organización primero.** Un permiso que el cliente no contrató no se lo
 *    puede conceder nadie —ni un super, porque `permisos_efectivos` lo descuenta
 *    de los suyos antes de que pueda repartirlo— así que decirle a quien
 *    administra que «no puedes conceder lo que no tienes» lo mandaría a pedirle
 *    un permiso a alguien que tampoco lo puede dar.
 * 2. **`marcado` importa, porque estrechar no es ensanchar.** Quitarle a otra
 *    cuenta un permiso que uno no lleva **sí** se puede si esa cuenta no puede
 *    más que uno, y el servidor lo acepta (`fuera_de_alcance` mira sólo lo que
 *    se otorga). Bloquear el tilde en los dos sentidos le impediría a un
 *    administrador sin exportación sacársela a alguien.
 */
export function motivoDeTildeApagado(
  permiso: Permiso,
  alcance: AlcanceDePermisos,
  marcado: boolean,
): MotivoDeTildeApagado | null {
  if (!alcance.disponibles.has(permiso)) return 'organizacion';
  if (!marcado && !alcance.otorgables.has(permiso)) return 'quien-edita';
  return null;
}
